import React, { useCallback, useEffect, useRef, useState } from 'react';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { useDirtyForm } from '../../../hooks/useDirtyForm';
import { usePermesso } from '../../../context/PermessiContext';
import { showToast } from '../../../components/Toast';
import { GIORNI_SETTIMANA } from './percorsoTipi';
import { API_DOCS } from './ConfigurazionePercorsoPage.api';

interface Orario { giornoSettimana: number; oraInizio: string; oraFine: string }
interface VenditoreSede { utenteId: number; calendarioEmail: string | null; attivo: boolean; utente: { id: number; nome: string; email: string } }
interface SedeConfig {
  id: number;
  nome: string;
  percorso: { urlPrenotazioni: string | null; telefono: string | null; emailContatto: string | null } | null;
  orari: Orario[];
  venditoriPrenotabili: VenditoreSede[];
}
interface Configurazione {
  societaId: number;
  validitaMesiAtrio: number; validitaMesiAran: number;
  condizioniAtrio: string | null; condizioniAran: string | null;
  indirizzoAtrio: string | null; indirizzoAran: string | null;
}
interface UtenteOpzione { id: number; nome: string; email: string }
interface DatiConfigurazione { configurazione: Configurazione; sedi: SedeConfig[]; utenti: UtenteOpzione[] }

const MOD = 'commerciale.percorso_configurazione';

const formBuoni = (c: Configurazione) => ({
  validitaMesiAtrio: String(c.validitaMesiAtrio),
  validitaMesiAran: String(c.validitaMesiAran),
  condizioniAtrio: c.condizioniAtrio ?? '',
  condizioniAran: c.condizioniAran ?? '',
  indirizzoAtrio: c.indirizzoAtrio ?? '',
  indirizzoAran: c.indirizzoAran ?? '',
});

function CardBuoni({ societaId, iniziale, canEdit }: { societaId: string; iniziale: Configurazione; canEdit: boolean }) {
  // La card nasce già con i dati caricati (key = società): lo snapshot parte dal form iniziale.
  const [form, setForm] = useState(() => formBuoni(iniziale));
  const [initialSnapshot, setInitialSnapshot] = useState(() => JSON.stringify(formBuoni(iniziale)));
  const [salvando, setSalvando] = useState(false);
  const isDirty = JSON.stringify(form) !== initialSnapshot;
  useDirtyForm(isDirty, 'Configurazione buoni');
  const campo = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm(f => ({ ...f, [k]: e.target.value }));

  const salva = async () => {
    setSalvando(true);
    try {
      // @api PUT /api/percorso-acquirenti/configurazione {"societaId":"number","validitaMesiAtrio":"number","validitaMesiAran":"number"} -> {"success":true}
      await apiFetch('/api/percorso-acquirenti/configurazione', { method: 'PUT', body: JSON.stringify({ societaId: Number(societaId), ...form }) });
      setInitialSnapshot(JSON.stringify(form));
      showToast('Configurazione dei buoni salvata');
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="bop-card">
      <div className="bop-card__header"><h2 className="bop-card__title">Buoni Atrio e Aran</h2></div>
      <div className="bop-card__body bop-percorso__sezione">
        <div className="bop-percorso__griglia">
          <div className="bop-percorso__sezione">
            <div className="bop-form-group">
              <label className="bop-label">Validità buono Atrio (mesi)</label>
              <input className="bop-input" type="number" min={1} max={60} value={form.validitaMesiAtrio} onChange={campo('validitaMesiAtrio')} disabled={!canEdit} />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Indirizzo Atrio stampato sul buono</label>
              <input className="bop-input" value={form.indirizzoAtrio} onChange={campo('indirizzoAtrio')} disabled={!canEdit} placeholder="Atrio Ghisalba, via Provinciale 28" />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Condizioni buono Atrio</label>
              <textarea className="bop-input bop-percorso__textarea" value={form.condizioniAtrio} onChange={campo('condizioniAtrio')} disabled={!canEdit} />
            </div>
          </div>
          <div className="bop-percorso__sezione">
            <div className="bop-form-group">
              <label className="bop-label">Validità buono Aran (mesi)</label>
              <input className="bop-input" type="number" min={1} max={60} value={form.validitaMesiAran} onChange={campo('validitaMesiAran')} disabled={!canEdit} />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Indirizzo Aran stampato sul buono</label>
              <input className="bop-input" value={form.indirizzoAran} onChange={campo('indirizzoAran')} disabled={!canEdit} placeholder="Aran Cucine Curno, via Lecco 83" />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Condizioni buono Aran</label>
              <textarea className="bop-input bop-percorso__textarea" value={form.condizioniAran} onChange={campo('condizioniAran')} disabled={!canEdit} />
            </div>
          </div>
        </div>
        {canEdit && (
          <div className="bop-percorso__riga">
            <button className="bop-btn bop-btn-primary" onClick={salva} disabled={salvando}>{salvando ? 'Salvataggio…' : 'Salva'}</button>
          </div>
        )}
      </div>
    </div>
  );
}

interface VenditoreForm { utenteId: number; scelto: boolean; calendarioEmail: string; attivo: boolean }

const nuovoVenditore = (utenteId: number): VenditoreForm => ({ utenteId, scelto: false, calendarioEmail: '', attivo: true });

const orariIniziali = (sede: SedeConfig): Orario[] =>
  sede.orari.map(o => ({ giornoSettimana: o.giornoSettimana, oraInizio: o.oraInizio, oraFine: o.oraFine }));
const venditoriIniziali = (sede: SedeConfig, utenti: UtenteOpzione[]): VenditoreForm[] =>
  utenti.map(u => {
    const v = sede.venditoriPrenotabili.find(x => x.utenteId === u.id);
    return { utenteId: u.id, scelto: Boolean(v), calendarioEmail: v?.calendarioEmail ?? '', attivo: v?.attivo ?? true };
  });

function CardSede({ sede, utenti, canEdit, onSalvata }: { sede: SedeConfig; utenti: UtenteOpzione[]; canEdit: boolean; onSalvata: () => void }) {
  const urlIniziale = sede.percorso?.urlPrenotazioni ?? '';
  const telefonoIniziale = sede.percorso?.telefono ?? '';
  const emailIniziale = sede.percorso?.emailContatto ?? '';
  const [url, setUrl] = useState(urlIniziale);
  const [telefono, setTelefono] = useState(telefonoIniziale);
  const [email, setEmail] = useState(emailIniziale);
  const [orari, setOrari] = useState<Orario[]>(() => orariIniziali(sede));
  const [venditori, setVenditori] = useState<VenditoreForm[]>(() => venditoriIniziali(sede, utenti));
  const [salvando, setSalvando] = useState(false);
  // La card nasce già con i dati caricati: lo snapshot parte dai valori iniziali e si aggiorna dopo ogni salvataggio OK.
  const [initialSnapshot, setInitialSnapshot] = useState(() =>
    JSON.stringify({ url: urlIniziale, telefono: telefonoIniziale, email: emailIniziale, orari: orariIniziali(sede), venditori: venditoriIniziali(sede, utenti) }));
  const isDirty = JSON.stringify({ url, telefono, email, orari, venditori }) !== initialSnapshot;
  useDirtyForm(isDirty, `Configurazione sede ${sede.nome}`);

  const cambiaOrario = (i: number, k: keyof Orario, valore: string) =>
    setOrari(lista => lista.map((o, j) => (j === i ? { ...o, [k]: k === 'giornoSettimana' ? Number(valore) : valore } : o)));
  // Un utente aggiunto alla società dopo il montaggio non ha ancora una riga: la si crea alla prima modifica.
  const cambiaVenditore = (utenteId: number, modifica: Partial<VenditoreForm>) =>
    setVenditori(lista => (lista.some(v => v.utenteId === utenteId)
      ? lista.map(v => (v.utenteId === utenteId ? { ...v, ...modifica } : v))
      : [...lista, { ...nuovoVenditore(utenteId), ...modifica }]));

  const salva = async () => {
    setSalvando(true);
    try {
      const corpo = {
        urlPrenotazioni: url, telefono, emailContatto: email, orari,
        venditori: venditori.filter(v => v.scelto).map(v => ({ utenteId: v.utenteId, calendarioEmail: v.calendarioEmail, attivo: v.attivo })),
      };
      // @api PUT /api/percorso-acquirenti/sedi/:sedeId {"urlPrenotazioni":"string","orari":"array","venditori":"array"} -> {"success":true}
      await apiFetch(`/api/percorso-acquirenti/sedi/${sede.id}`, { method: 'PUT', body: JSON.stringify(corpo) });
      setInitialSnapshot(JSON.stringify({ url, telefono, email, orari, venditori }));
      showToast(`Sede ${sede.nome} salvata`);
      onSalvata();
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="bop-card">
      <div className="bop-card__header"><h2 className="bop-card__title">{sede.nome}</h2></div>
      <div className="bop-card__body bop-percorso__sezione">
        <div className="bop-form-row">
          <div className="bop-form-group">
            <label className="bop-label">Indirizzo della pagina di prenotazione</label>
            <input className="bop-input" value={url} onChange={e => setUrl(e.target.value)} disabled={!canEdit} placeholder="https://prenota.bgres.it" />
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Telefono del negozio</label>
            <input className="bop-input" value={telefono} onChange={e => setTelefono(e.target.value)} disabled={!canEdit} />
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Email di contatto</label>
            <input className="bop-input" value={email} onChange={e => setEmail(e.target.value)} disabled={!canEdit} />
          </div>
        </div>

        <h3 className="bop-card__title">Orari di apertura</h3>
        {orari.length === 0 && <div className="bop-empty-state">Nessun orario: la pagina non mostrerà slot</div>}
        {orari.map((o, i) => (
          <div className="bop-percorso__orario-riga" key={i}>
            <select className="bop-select" value={o.giornoSettimana} onChange={e => cambiaOrario(i, 'giornoSettimana', e.target.value)} disabled={!canEdit}>
              {GIORNI_SETTIMANA.slice(1).map((g, n) => <option key={g} value={n + 1}>{g}</option>)}
            </select>
            <input className="bop-input" type="time" value={o.oraInizio} onChange={e => cambiaOrario(i, 'oraInizio', e.target.value)} disabled={!canEdit} />
            <input className="bop-input" type="time" value={o.oraFine} onChange={e => cambiaOrario(i, 'oraFine', e.target.value)} disabled={!canEdit} />
            {canEdit && <button className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => setOrari(lista => lista.filter((_, j) => j !== i))}>Elimina</button>}
          </div>
        ))}
        {canEdit && (
          <div className="bop-percorso__riga">
            <button className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => setOrari(lista => [...lista, { giornoSettimana: 2, oraInizio: '09:00', oraFine: '12:30' }])}>+ Aggiungi fascia</button>
          </div>
        )}

        <h3 className="bop-card__title">Venditori prenotabili</h3>
        <table className="bop-table">
          <thead>
            <tr><th>Prenotabile</th><th>Utente</th><th>Email del calendario (se diversa)</th><th>Attivo</th></tr>
          </thead>
          <tbody>
            {utenti.map(u => {
              const v = venditori.find(x => x.utenteId === u.id) ?? nuovoVenditore(u.id);
              return (
                <tr key={u.id}>
                  <td><input type="checkbox" checked={v.scelto} onChange={e => cambiaVenditore(u.id, { scelto: e.target.checked })} disabled={!canEdit} /></td>
                  <td>{u.nome}<br /><span className="bop-text-muted">{u.email}</span></td>
                  <td><input className="bop-input" value={v.calendarioEmail} onChange={e => cambiaVenditore(u.id, { calendarioEmail: e.target.value })} disabled={!canEdit || !v.scelto} placeholder={u.email} /></td>
                  <td><input type="checkbox" checked={v.attivo} onChange={e => cambiaVenditore(u.id, { attivo: e.target.checked })} disabled={!canEdit || !v.scelto} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {canEdit && (
          <div className="bop-percorso__riga">
            <button className="bop-btn bop-btn-primary" onClick={salva} disabled={salvando}>{salvando ? 'Salvataggio…' : `Salva ${sede.nome}`}</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ConfigurazionePercorsoPage() {
  const { societaId, pronta } = useFiltroSocieta();
  const { can } = usePermesso();
  const canEdit = can(MOD, 'edit');
  const [dati, setDati] = useState<DatiConfigurazione | null>(null);
  const [errore, setErrore] = useState('');
  const richiestaCorrente = useRef(0);

  // azzera = true: si svuota la pagina prima di caricare (cambio società), così non resta mai
  // visibile né salvabile il form di un'altra società. Dopo il salvataggio di una sede si passa false:
  // ricarica senza svuotare, per non perdere le modifiche non salvate negli altri riquadri.
  const carica = useCallback((azzera = true) => {
    // Conta le richieste: la risposta di una richiesta superata da una più recente va ignorata.
    const richiesta = ++richiestaCorrente.current;
    setErrore('');
    if (azzera) setDati(null);
    if (!pronta || !societaId) {
      setDati(null);
      return;
    }
    // @api GET /api/percorso-acquirenti/configurazione?societaId -> {"success":true,"data":{"configurazione":"object","sedi":"array","utenti":"array"}}
    apiFetch<{ success: boolean; data: DatiConfigurazione }>(`/api/percorso-acquirenti/configurazione?societaId=${societaId}`)
      .then(r => { if (richiesta === richiestaCorrente.current) setDati(r.data); })
      .catch(e => { if (richiesta === richiestaCorrente.current) setErrore(e.message); });
  }, [pronta, societaId]);

  useEffect(() => { carica(); }, [carica]);

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h1 className="bop-page-title">Configurazione percorso acquirenti</h1>
          <p className="bop-page-subtitle">Venditori e orari prenotabili per negozio, pagina pubblica, buoni Atrio e Aran</p>
        </div>
      </div>
      {!societaId && <div className="bop-empty-state">Seleziona una società in alto</div>}
      {errore && <div className="bop-alert bop-alert--danger">{errore}</div>}
      {dati && (
        <div className="bop-percorso__sezione">
          <CardBuoni key={`buoni-${dati.configurazione.societaId}`} societaId={String(dati.configurazione.societaId)} iniziale={dati.configurazione} canEdit={canEdit} />
          {dati.sedi.map(s => <CardSede key={`${s.id}-${s.venditoriPrenotabili.length}-${s.orari.length}`} sede={s} utenti={dati.utenti} canEdit={canEdit} onSalvata={() => carica(false)} />)}
        </div>
      )}
      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
